import { ArrowLeft, ClipboardList, Download, Info, Printer, RefreshCw } from 'lucide-react'
import type { ReactNode } from 'react'

import { api, API_BASE } from '@/api/client'
import { CostNote } from '@/components/CostNote'
import { Icon } from '@/components/Icon'
import { LoadingBlock } from '@/components/LoadingBlock'
import { NoticeList } from '@/components/NoticeList'
import { SignalCount } from '@/components/SignalBadge'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { SourceLine } from '@/features/report/Computed'
import { ConfidenceTag } from '@/features/report/ConfidenceTag'
import { Explainer } from '@/features/report/Explainer'
import { RunDiffView } from '@/features/facts/RunDiffView'
import { FindingCard, IndicativeCard } from '@/features/report/FindingCard'
import { groupSkipped } from '@/features/report/skippedReasons'
import { DraftBadge, DraftNote } from '@/features/rulebase/DraftBadge'
import { isDraft } from '@/features/rulebase/rulebase'
import { ErrorNote } from '@/components/ErrorNote'
import { useI18n } from '@/i18n'
import { Link, buildPath, navigate } from '@/lib/router'
import { useAsync } from '@/lib/useAsync'
import type { GemeindePack, Run, RunDiff, RunReport, Severity } from '@/api/types'

const SEVERITY_ORDER: Record<Severity, number> = { red: 0, amber: 1, green: 2 }

export type ReportVariant = 'screen' | 'print'

export function ReportView({
  runId,
  onBack,
  onNewRun,
}: {
  runId: string
  onBack: () => void
  onNewRun: () => void
}) {
  const { t } = useI18n()
  const report = useAsync<RunReport>((signal) => api.report(runId, signal), [runId])
  // The run carries what the report cannot: the re-run relationship
  // (`mode`, `parent_run_id`) and the founder-only cost.
  const run = useAsync<Run>((signal) => api.run(runId, signal), [runId])
  // The rulebase packs carry the document version register — which BZO, which
  // Stand. A finding only names a version when its own rule inlines one; the
  // register is the honest place for the rest, and it is never matched to a
  // finding by string similarity.
  const packs = useAsync<GemeindePack[]>((signal) => api.gemeinden(undefined, signal), [])
  const parentRunId = run.data?.parent_run_id ?? null
  // Only ask for a diff when there is something to compare against — the
  // endpoint 404s on a comparison partner that does not exist, and a report
  // with no predecessor has nothing to show anyway.
  const diff = useAsync<RunDiff | null>(
    (signal) => (parentRunId ? api.runDiff(runId, undefined, signal) : Promise.resolve(null)),
    [runId, parentRunId],
  )

  if (report.loading) return <LoadingBlock />
  if (report.error || !report.data)
    return (
      <ErrorNote
        message={`${t('report.loadError')} ${report.error ?? ''}`}
        onRetry={report.reload}
        retryLabel={t('common.retry')}
      />
    )

  return (
    <Report
      report={report.data}
      run={run.data}
      diff={diff.data}
      packs={packs.data}
      onBack={onBack}
      onNewRun={onNewRun}
    />
  )
}

export function Report({
  report,
  run,
  diff,
  packs,
  variant = 'screen',
  onBack,
  onNewRun,
}: {
  report: RunReport
  run?: Run | null
  diff?: RunDiff | null
  packs?: GemeindePack[] | null
  variant?: ReportVariant
  onBack?: () => void
  onNewRun?: () => void
}) {
  const { t } = useI18n()
  const isPrint = variant === 'print'
  // Presentation order only: red before amber before green, engine order kept
  // inside each group. The engine decides severities; the reader should not
  // have to hunt for the red ones.
  const findings = [...(report.findings ?? [])].sort(
    (a, b) => SEVERITY_ORDER[a.severity] - SEVERITY_ORDER[b.severity],
  )
  const indicative = report.indicative_findings ?? []
  const hinweise = report.hinweise ?? []
  const nachweise = report.nachweise ?? []
  const skipped = report.skipped ?? []
  const skippedGroups = groupSkipped(skipped)
  const grenzen = report.grenzen ?? []
  // Exact match on the same field the engine resolved the pack with — never a
  // fuzzy name match.
  const pack = packs?.find((candidate) => candidate.gemeinde === report.gemeinde) ?? null
  // M4 — indicative mode, visibly. `report.mode` on its own is NOT the answer:
  // a partly verified draft runs with `mode: "verified"` while its pack is
  // still a draft, and reading the mode alone would drop the label in exactly
  // that state. The pack the run stood on (`pack_coverage`) is the primary
  // source; the register is the fallback for an older report without it.
  const packIsDraft = report.pack_coverage
    ? isDraft(report.pack_coverage)
    : pack
      ? isDraft(pack)
      : false
  // The backend's own sentence for the state, whichever of the two it is.
  const draftMessage =
    (report.notices ?? []).find((notice) => (notice.code as string) === 'draft_pack')
      ?.message_de ??
    report.pack_coverage?.status_note_de ??
    null
  const cardGrid = isPrint ? 'grid gap-4' : 'grid gap-4 md:grid-cols-2'

  return (
    <div className="flex flex-col gap-12">
      <header className="flex flex-col gap-3">
        {onBack && !isPrint ? (
          <Button variant="ghost" size="sm" className="self-start" onClick={onBack}>
            <Icon icon={ArrowLeft} />
            {t('common.back')}
          </Button>
        ) : null}
        <h1 className="font-display text-[32px] leading-tight font-semibold">{t('report.title')}</h1>
        <dl className="flex flex-wrap gap-x-8 gap-y-2 text-[13px]">
          {/* M4: a draft Gemeinde is labelled wherever it appears, with the
              same dashed grey treatment as everywhere else. */}
          <Meta
            label={t('report.gemeinde')}
            value={report.gemeinde}
            badge={packIsDraft ? <DraftBadge /> : undefined}
          />
          <Meta label={t('report.zone')} value={report.zone} />
          {report.regime ? <Meta label={t('report.regime')} value={report.regime} /> : null}
          {report.case_id ? <Meta label={t('report.case')} value={report.case_id} /> : null}
          <Meta label={t('run.id')} value={report.run_id} />
        </dl>

        {packIsDraft ? <DraftNote gemeinde={report.gemeinde} message={draftMessage} /> : null}

        {report.mode === 'indicative' ? (
          <p
            role="status"
            data-testid="indicative-mode-banner"
            className="flex items-start gap-2 rounded-sm border border-dashed border-border-strong bg-surface-2 px-4 py-3 text-[13px] text-muted"
          >
            <Icon icon={Info} className="mt-0.5 text-muted-2" />
            {t('report.modeIndicativeBanner')}
          </p>
        ) : null}

        {/* A re-run says so, and says what it is a re-run of. */}
        {run?.mode === 'recheck' ? (
          <div
            role="note"
            data-testid="report-recheck-banner"
            className="flex flex-wrap items-center gap-3 rounded-sm border border-border bg-surface px-4 py-3 text-[13px] text-ink-2"
          >
            <Icon icon={RefreshCw} />
            <span>{t('report.isRecheck')}</span>
            {run.parent_run_id && !isPrint ? (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => navigate(buildPath('report', { runId: run.parent_run_id! }))}
              >
                {t('report.openParentReport')}
              </Button>
            ) : null}
          </div>
        ) : null}
      </header>

      {/* Why this run is the way it is. Rendered before the findings, because a
          degraded run must never be read as a clean one. */}
      <NoticeList notices={report.notices} title={t('notices.reportTitle')} />

      {/* ---- Ampel — hard findings only ---------------------------------- */}
      <Section title={t('report.ampelTitle')} lead={t('report.ampelLead')}>
        <div className={isPrint ? 'grid gap-4 grid-cols-3' : 'grid gap-4 sm:grid-cols-3'}>
          <SignalCount severity="red" count={report.ampel.red ?? 0} />
          <SignalCount severity="amber" count={report.ampel.amber ?? 0} />
          <SignalCount severity="green" count={report.ampel.green ?? 0} />
        </div>
      </Section>

      {/* ---- How to read this report — first-class, right after the counts */}
      <Explainer variant={variant} />

      {/* ---- What changed against the previous run ------------------------ */}
      {diff ? (
        <Section title={t('diff.sectionTitle')} lead={t('diff.sectionLead')}>
          <div className="rounded-md border border-border bg-surface p-6">
            <RunDiffView diff={diff} />
          </div>
        </Section>
      ) : null}

      {/* ---- Hard findings ------------------------------------------------ */}
      <Section title={t('report.findingsTitle')} lead={t('report.findingsLead')}>
        {findings.length === 0 ? (
          <Empty>{t('report.findingsEmpty')}</Empty>
        ) : (
          <ul data-testid="hard-findings" className={cardGrid}>
            {findings.map((finding) => (
              <li key={finding.rule_id}>
                <FindingCard finding={finding} />
              </li>
            ))}
          </ul>
        )}
      </Section>

      {/* ---- Indicative — a separate list, never merged ------------------- */}
      <Section title={t('report.indicativeTitle')} lead={t('report.indicativeLead')}>
        {indicative.length === 0 ? (
          <Empty>{t('report.indicativeEmpty')}</Empty>
        ) : (
          <ul data-testid="indicative-findings" className={cardGrid}>
            {indicative.map((finding) => (
              <li key={finding.rule_id}>
                <IndicativeCard finding={finding} />
              </li>
            ))}
          </ul>
        )}
      </Section>

      {/* ---- Manuell · zu prüfen -----------------------------------------
          One of the three honest sections. It renders ALWAYS — an empty lane
          gets an empty state that says what the emptiness does and does not
          mean, never nothing at all. */}
      <Section title={t('report.skippedTitle')} lead={t('report.skippedLead')}>
        {skipped.length === 0 ? (
          <Empty data-testid="skipped-empty">{t('report.skippedEmpty')}</Empty>
        ) : (
          /* Grouped by the kind of reason, blocking groups first — see
             features/report/skippedReasons.ts. Nothing is hidden and nothing is
             collapsed: every rule that did not run is still on this page, in a
             list under a heading that says what its group means. */
          <div data-testid="skipped-rules" className="flex flex-col gap-6">
            {skippedGroups.map((group) => (
              <section
                key={group.kind}
                aria-labelledby={`skipped-${group.kind}`}
                data-testid="skipped-group"
                data-kind={group.kind}
                data-blocking={group.blocking}
                data-count={group.rules.length}
                className="flex flex-col gap-3"
              >
                <div className="break-after-avoid">
                  <h3
                    id={`skipped-${group.kind}`}
                    className="font-display text-[15px] font-semibold text-ink"
                  >
                    {t(group.title)}{' '}
                    <span className="num font-normal text-muted-2">({group.rules.length})</span>
                  </h3>
                  <p className="mt-0.5 max-w-[80ch] text-[12px] text-muted">{t(group.lead)}</p>
                </div>
                <ul className="flex flex-col divide-y divide-border rounded-md border border-border bg-white">
                  {group.rules.map((rule, index) => (
                    <li
                      key={`${rule.rule_id ?? 'rule'}-${index}`}
                      className="flex flex-col gap-2 p-4 break-inside-avoid"
                    >
                      {rule.title ? <p className="text-[14px] text-ink">{rule.title}</p> : null}
                      <p className="text-[13px] text-muted">
                        <span className="text-muted-2">{t('report.skippedReason')}:</span>{' '}
                        {rule.reason}
                      </p>
                      <SourceLine ruleId={rule.rule_id} source={rule.source} pack={rule.pack} />
                    </li>
                  ))}
                </ul>
              </section>
            ))}
          </div>
        )}
      </Section>

      {/* ---- Vorbehaltene Nachweise -------------------------------------- */}
      <Section title={t('report.nachweiseTitle')} lead={t('report.nachweiseLead')}>
        {nachweise.length === 0 ? (
          <Empty data-testid="nachweise-empty">{t('report.nachweiseEmpty')}</Empty>
        ) : (
          <ul
            data-testid="nachweise"
            className="flex flex-col divide-y divide-border rounded-md border border-border bg-white"
          >
            {nachweise.map((nachweis) => (
              <li key={nachweis.rule_id} className="flex flex-col gap-2 p-4 break-inside-avoid">
                <div className="flex flex-wrap items-center gap-3">
                  <span className="text-[14px] text-ink">{nachweis.title ?? nachweis.rule_id}</span>
                  <Badge variant="neutral">
                    {nachweis.erbracht ? t('report.nachweisDone') : t('report.nachweisOpen')}
                  </Badge>
                  {nachweis.due ? (
                    <span className="text-[12px] text-muted">
                      {t('report.nachweisDue')}: <span className="num">{nachweis.due}</span>
                    </span>
                  ) : null}
                </div>
                <SourceLine ruleId={nachweis.rule_id} source={nachweis.source} />
              </li>
            ))}
          </ul>
        )}
      </Section>

      {/* ---- Hinweise — the Indikator lane -------------------------------- */}
      <Section title={t('report.hinweiseTitle')} lead={t('report.hinweiseLead')}>
        {hinweise.length === 0 ? (
          <Empty data-testid="hinweise-empty">{t('report.hinweiseEmpty')}</Empty>
        ) : (
          <ul data-testid="hinweise" className={cardGrid}>
            {hinweise.map((hinweis) => (
              <li
                key={hinweis.rule_id}
                className="flex h-full flex-col gap-2 rounded-md border border-border bg-surface p-4 break-inside-avoid"
              >
                <div className="flex flex-wrap items-center gap-2">
                  <Badge variant="neutral">{t('report.hinweisBadge')}</Badge>
                  <ConfidenceTag lane="hinweis" />
                  {hinweis.confidence && hinweis.confidence !== '—' ? (
                    <span className="text-[12px] text-muted">
                      {t('report.confidence')}: <span className="num">{hinweis.confidence}</span>
                    </span>
                  ) : null}
                </div>
                <p className="text-[13px] leading-snug text-ink-2">{hinweis.message}</p>
                <SourceLine ruleId={hinweis.rule_id} source={hinweis.source} />
              </li>
            ))}
          </ul>
        )}
      </Section>

      {/* ---- Grenzen ------------------------------------------------------ */}
      <Section title={t('report.grenzenTitle')} lead={t('report.grenzenLead')}>
        {grenzen.length === 0 ? (
          <Empty data-testid="grenzen-empty">{t('report.grenzenEmpty')}</Empty>
        ) : (
          <ul
            data-testid="grenzen"
            className="flex list-disc flex-col gap-2 rounded-md border border-border bg-surface p-6 ps-10 text-[13px] text-muted"
          >
            {grenzen.map((limit) => (
              <li key={limit}>{limit}</li>
            ))}
          </ul>
        )}
      </Section>

      {/* ---- Quellen und Stand — the pack's document version register ----- */}
      {pack ? (
        <Section title={t('report.sourcesTitle')} lead={t('report.sourcesLead')}>
          {(pack.sources ?? []).length === 0 ? (
            <Empty>{t('report.sourcesEmpty')}</Empty>
          ) : (
            <ul
              data-testid="pack-sources"
              className="flex flex-col divide-y divide-border rounded-md border border-border bg-white text-[13px]"
            >
              {(pack.sources ?? []).map((source) => (
                <li
                  key={source.key}
                  className="flex flex-wrap items-baseline gap-x-3 gap-y-1 p-4 break-inside-avoid"
                >
                  <span className="text-ink">{source.doc ?? source.key}</span>
                  <span className="num text-[12px] text-muted">
                    {source.version
                      ? `${t('report.asOf')} ${source.version}`
                      : t('report.sourceNoVersion')}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </Section>
      ) : null}

      <p
        data-testid="disclaimer"
        className="max-w-[80ch] rounded-md border border-border bg-signal-info-soft p-4 text-[13px] text-signal-info break-inside-avoid"
      >
        {report.disclaimer}
      </p>

      {/* Paper carries no controls. */}
      {isPrint ? null : (
        <div className="flex flex-wrap gap-3">
          {run ? (
            <Button
              variant="secondary"
              onClick={() =>
                navigate(
                  buildPath('facts', {
                    workspaceId: run.workspace_id,
                    dossierId: run.dossier_id,
                  }),
                )
              }
            >
              <Icon icon={ClipboardList} />
              {t('facts.cta')}
            </Button>
          ) : null}
          <Link
            to={buildPath('reportPrint', { runId: report.run_id })}
            data-testid="print-link"
            className="inline-flex items-center gap-2 rounded-sm border border-border-strong bg-white px-4 py-2 text-[14px] text-ink-2"
          >
            <Icon icon={Printer} />
            {t('report.printView')}
          </Link>
          <a
            href={`${API_BASE}/runs/${encodeURIComponent(report.run_id)}/report.pdf`}
            data-testid="pdf-link"
            className="inline-flex items-center gap-2 rounded-sm border border-border-strong bg-white px-4 py-2 text-[14px] text-ink-2"
          >
            <Icon icon={Download} />
            {t('report.pdfDownload')}
          </a>
          {onNewRun ? (
            <Button variant="secondary" onClick={onNewRun}>
              {t('report.newRun')}
            </Button>
          ) : null}
        </div>
      )}

      {/* Founder-only, internal, and never on paper; `null` renders nothing. */}
      {isPrint ? null : <CostNote cost={run?.cost} />}
    </div>
  )
}

function Section({ title, lead, children }: { title: string; lead?: string; children: ReactNode }) {
  return (
    <section className="flex flex-col gap-4">
      <div className="break-after-avoid">
        <h2 className="font-display text-[21px] font-semibold">{title}</h2>
        {lead ? <p className="mt-1 max-w-[80ch] text-[13px] text-muted">{lead}</p> : null}
      </div>
      {children}
    </section>
  )
}

function Meta({ label, value, badge }: { label: string; value: string; badge?: ReactNode }) {
  return (
    <div className="flex items-center gap-2">
      <dt className="text-muted">{label}</dt>
      <dd className="num flex items-center gap-2 text-ink-2">
        {value}
        {badge}
      </dd>
    </div>
  )
}

function Empty({ children, ...rest }: { children: ReactNode; 'data-testid'?: string }) {
  return (
    <p
      {...rest}
      className="rounded-md border border-border bg-surface px-4 py-3 text-[13px] text-muted"
    >
      {children}
    </p>
  )
}
